import React, { useEffect } from "react";
import {
  IconButton,
  Tooltip,
} from "@chakra-ui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHeadphones } from "@fortawesome/free-solid-svg-icons";

export default function TextToSpeechBox({
  onRegister,
  onToggle
}: {
  onRegister: (callback: (bytes: any, sampleRate: number) => void) => void
  onToggle: (isEnabled: boolean) => void
}) {

  const [isEnabled, setIsEnabled] = React.useState(false);
  const textToSpeechQueue = React.useRef<Int16Array[]>([]);
  const audioContext = React.useRef<AudioContext>();
   const nextStartTime = React.useRef(0);

  const isPlaying = React.useRef(false);

  const toggleTextToSpeech = () => {
    if (!isEnabled) {
      enableAudioContext();
    } else {
      isPlaying.current = false;
      disableAudioContext();
    }
    onToggle(!isEnabled);
    setIsEnabled(previousState => !previousState);
  }

  const incomingData = (audioBytes: any, sampleRate: number = 24000) => {
    if (!audioContext.current) return;
    playAudio(audioBytes, sampleRate);
  };

  const playAudio = (audioBytes: any, sampleRate: number) => {
      if (!audioContext.current) return;

      const buffer = new Float32Array(audioBytes.length / 2);
      for (let i = 0; i < audioBytes.length; i += 2) {
        const value = audioBytes[i] + (audioBytes[i + 1] << 8);
        buffer[i / 2] =
          value >= 0x8000 ? -(0x10000 - value) / 0x8000 : value / 0x7fff;
      }

      const audioBuffer = audioContext.current.createBuffer(1, buffer.length, sampleRate);
      audioBuffer.copyToChannel(buffer, 0);

      const source = audioContext.current.createBufferSource();
      source.buffer = audioBuffer;

      // Add a short fade to avoid clicks
      const gainNode = audioContext.current.createGain();
      source.connect(gainNode).connect(audioContext.current.destination);

      const now = audioContext.current.currentTime;
      if (nextStartTime.current < now) {
        nextStartTime.current = now; // reset if we fell behind
      }

      // Short fade in/out
      gainNode.gain.setValueAtTime(0, nextStartTime.current);
      gainNode.gain.linearRampToValueAtTime(1, nextStartTime.current + 0.01);
      gainNode.gain.setValueAtTime(1, nextStartTime.current + audioBuffer.duration - 0.01);
      gainNode.gain.linearRampToValueAtTime(0, nextStartTime.current + audioBuffer.duration);

      source.start(nextStartTime.current);

      nextStartTime.current += audioBuffer.duration;
  };

  useEffect(() => {
    onRegister(incomingData);
    return () => {
      // Clean up audio context when component unmounts
      disableAudioContext();
    }
  }, []);

  const disableAudioContext = () => {
    if (audioContext.current) {
      audioContext.current.close();
      audioContext.current = undefined;
    }
  }

  const enableAudioContext = () => {
    audioContext.current = new AudioContext();

    const oscillator = audioContext.current.createOscillator();
    oscillator.frequency.value = 25000;

    oscillator.connect(audioContext.current.destination);
    oscillator.start();

    setTimeout(() => {
      oscillator.stop();
    }, 500);

  };

  return (
    <>
      <Tooltip label="Enable/Disable text to speech">
        <IconButton
          aria-label="Text to speech"
          variant="beOutline"
          onClick={toggleTextToSpeech}
          size="sm"
          backgroundColor={isEnabled ? 'primaryBlue' : '#ffffff'}
          borderRadius="50%"
          icon={<FontAwesomeIcon icon={faHeadphones} size={"sm"} color={!isEnabled ? 'primaryBlue' : '#ffffff'} />}
        />
      </Tooltip>
    </>
  );
}
